import type { FileMeta } from '../../../shared/transfer.js';
import { saveBlob } from '../download.js';
import { formatBytes } from '../format.js';
import { t } from '../i18n/index.js';
import { canShareFile, shareFile } from '../native-share.js';
import type { ReceivedFile } from '../receive-file.js';
import { el } from './dom.js';
import { userFacingError } from './shell.js';

/** "Open with…" hands the file to other apps through the OS share sheet where the browser allows it. */
const createOpenActions = (file: File, note: HTMLElement): HTMLElement => {
  const openButton = el('button', { className: 'button button--ghost', type: 'button', text: t('landed.openWith') });
  openButton.hidden = !canShareFile(navigator, file);
  openButton.addEventListener('click', () => {
    shareFile(file).catch((error: unknown) => {
      note.textContent = userFacingError(error);
      note.className = 'share__note share__note--warn';
    });
  });
  return el('div', { className: 'save__actions' }, [openButton]);
};

const createFollowUp = (): HTMLElement =>
  el('p', { className: 'card__text save__follow-up' }, [el('a', { className: 'link', href: '/', text: t('landed.sendYourself') })]);

/**
 * The bytes are already on disk; say where, and offer to open the file when the API lets us read it
 * back. The file handed to other apps is re-wrapped with the announced name and type: a save dialog
 * may rename the file ("photo.png (1)") and return it without a MIME type, which share sheets refuse.
 */
const createWrittenCard = (
  meta: FileMeta,
  location: 'chosen-folder' | 'downloads',
  savedName: string,
  file: File | null,
  withFollowUp: boolean,
): HTMLElement => {
  const note = el('div', { className: 'share__note' });
  const description = location === 'chosen-folder' ? t('landed.chosenFolder', { name: savedName }) : t('landed.downloads');
  const children: HTMLElement[] = [
    el('div', { className: 'card__title', text: t('landed.title') }),
    el('p', { className: 'card__text', text: description }),
  ];
  if (file !== null) {
    children.push(createOpenActions(new File([file], meta.name, { type: meta.mimeType }), note));
  }
  if (withFollowUp) {
    children.push(createFollowUp());
  }
  children.push(note);
  return el('section', { className: 'card save' }, children);
};

/** The bytes sit in Blob storage; one Save action, then the saved state with open actions. */
const createInMemoryCard = (meta: FileMeta, blob: Blob, withFollowUp: boolean): HTMLElement => {
  const file = new File([blob], meta.name, { type: meta.mimeType });
  const text = el('p', { className: 'card__text', text: t('memory.received', { size: formatBytes(meta.size) }) });
  const saveButton = el('button', { className: 'button button--large', type: 'button', text: t('memory.save') });
  const note = el('div', { className: 'share__note' });
  const afterChildren: HTMLElement[] = [createOpenActions(file, note)];
  if (withFollowUp) {
    afterChildren.push(createFollowUp());
  }
  const afterSave = el('div', { className: 'save__after' }, afterChildren);
  afterSave.hidden = true;
  saveButton.addEventListener('click', () => {
    saveBlob(blob, meta.name);
    text.textContent = t('memory.saved');
    saveButton.textContent = t('memory.saveAgain');
    saveButton.className = 'button button--large button--ghost';
    afterSave.hidden = false;
  });
  return el('section', { className: 'card save' }, [
    el('div', { className: 'card__title', text: t('landed.title') }),
    text,
    saveButton,
    afterSave,
    note,
  ]);
};

/** Renders what happened to a received file. `withFollowUp` adds the "send a file yourself" link. */
export const createOutcomeCard = (received: ReceivedFile, withFollowUp: boolean): HTMLElement =>
  received.outcome.kind === 'written'
    ? createWrittenCard(received.meta, received.outcome.location, received.outcome.savedName, received.outcome.file, withFollowUp)
    : createInMemoryCard(received.meta, received.outcome.blob, withFollowUp);
